import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, KeyRound, Loader2, Newspaper, Cookie, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/newsUtils";
import { FREQUENCIES } from "@/lib/onboardingTopics";
import { getConsent, openCookieSettings } from "@/lib/cookies";

const consentLabel = { accepted: "Aceptadas", rejected: "Rechazadas" };

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [savingDigest, setSavingDigest] = useState(false);
  const [savingFreq, setSavingFreq] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changing, setChanging] = useState(false);

  const sendDigestNow = async () => {
    setSendingNow(true);
    try {
      await axios.post(`${API}/me/send-digest`);
      toast.success("Te hemos enviado el resumen. Revisa tu correo (y spam).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el resumen");
    } finally {
      setSendingNow(false);
    }
  };

  const toggleDigest = async (value) => {
    setSavingDigest(true);
    try {
      await axios.patch(`${API}/me/settings`, { digest_enabled: value });
      await refreshUser();
      toast.success(value ? "Resumen por email activado" : "Resumen por email desactivado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el cambio");
    } finally {
      setSavingDigest(false);
    }
  };

  const changeFrequency = async (freq) => {
    if (freq === user?.digest_frequency) return;
    setSavingFreq(true);
    try {
      await axios.patch(`${API}/me/settings`, { digest_frequency: freq });
      await refreshUser();
      toast.success("Frecuencia actualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la frecuencia");
    } finally {
      setSavingFreq(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Las contraseñas nuevas no coinciden.");
      return;
    }
    setChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-lg font-black tracking-tight">ÁREA PRIVADA</h1>
          </div>
          <Button
            data-testid="settings-back-button"
            variant="outline"
            onClick={() => navigate("/")}
            className="rounded-none border-border h-10 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Volver al feed</span>
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* Account */}
        <section data-testid="settings-account">
          <h2 className="font-heading text-xl font-black tracking-tight mb-4">Tu cuenta</h2>
          <div className="border border-border divide-y divide-border">
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-sm text-muted-foreground uppercase tracking-widest">Usuario</span>
              <span className="font-semibold" data-testid="account-username">{user?.username}</span>
            </div>
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-sm text-muted-foreground uppercase tracking-widest">Correo</span>
              <span className="font-semibold" data-testid="account-email">{user?.email}</span>
            </div>
          </div>
        </section>

        {/* Digest toggle */}
        <section data-testid="settings-digest">
          <h2 className="font-heading text-xl font-black tracking-tight mb-4">Resumen por email</h2>
          <div className="border border-border px-5 py-5 flex items-start justify-between gap-6">
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Recibir Últimas Noticias por email</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Te enviamos un resumen con tus noticias más destacadas según la frecuencia que elijas.
                </p>
              </div>
            </div>
            <Switch
              data-testid="digest-toggle"
              checked={!!user?.digest_enabled}
              disabled={savingDigest}
              onCheckedChange={toggleDigest}
            />
          </div>

          {user?.digest_enabled && (
            <div className="border border-t-0 border-border px-5 py-5" data-testid="settings-frequency">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Frecuencia</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {FREQUENCIES.filter((f) => f.key !== "off").map((f) => {
                  const active = (user?.digest_frequency || "twice_week") === f.key;
                  return (
                    <button
                      key={f.key}
                      data-testid={`settings-frequency-${f.key}`}
                      onClick={() => changeFrequency(f.key)}
                      disabled={savingFreq}
                      className={`p-3.5 border text-left transition-all duration-200 ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-foreground"
                      }`}
                    >
                      <p className="font-heading font-black tracking-tight text-sm flex items-center gap-2 flex-wrap">
                        {f.label}
                        {f.recommended && (
                          <span className="text-[9px] uppercase tracking-wide bg-primary text-white px-1.5 py-0.5">Rec.</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            data-testid="send-digest-now-button"
            variant="outline"
            onClick={sendDigestNow}
            disabled={sendingNow}
            className="rounded-none border-border h-10 gap-2 mt-3"
          >
            {sendingNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviarme el resumen ahora
          </Button>
        </section>

        <Separator />

        {/* Change password */}
        <section data-testid="settings-password">
          <h2 className="font-heading text-xl font-black tracking-tight mb-4">Cambiar contraseña</h2>
          <form onSubmit={changePassword} className="border border-border px-5 py-5 space-y-4">
            <div>
              <Label htmlFor="current_password">Contraseña actual</Label>
              <Input
                id="current_password"
                type="password"
                data-testid="current-password-input"
                className="rounded-none mt-1"
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="new_password">Nueva contraseña</Label>
              <Input
                id="new_password"
                type="password"
                data-testid="new-password-input"
                className="rounded-none mt-1"
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Repetir nueva contraseña</Label>
              <Input
                id="confirm_password"
                type="password"
                data-testid="confirm-password-input"
                className="rounded-none mt-1"
                value={pwd.confirm}
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="change-password-button"
              disabled={changing}
              className="rounded-none bg-primary hover:bg-blue-900 h-10 gap-2"
            >
              {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Actualizar contraseña
            </Button>
          </form>
        </section>
        <Separator />

        {/* Cookies */}
        <section data-testid="settings-cookies">
          <h2 className="font-heading text-xl font-black tracking-tight mb-4">Cookies</h2>
          <div className="border border-border px-5 py-5 flex items-start justify-between gap-6">
            <div className="flex gap-3">
              <Cookie className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Preferencias de cookies</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Estado actual:{" "}
                  <span className="font-semibold text-foreground" data-testid="cookie-consent-status">
                    {consentLabel[getConsent()] || "Sin decidir"}
                  </span>
                  .
                </p>
              </div>
            </div>
            <Button
              data-testid="manage-cookies-button"
              variant="outline"
              onClick={openCookieSettings}
              className="rounded-none border-border h-10 shrink-0"
            >
              Gestionar cookies
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
